import React, {useContext, useEffect, useRef, useState} from "react";
import UpdateIcon from '@mui/icons-material/Update';
import {ErrorBoundary} from 'react-error-boundary';
import Navigation from "./nav/Navigation";
import NavigationDevelopment from "./nav/NavigationDevelopment";
import Title from "@common/Title";
import Body from "@common/Body";
import ContextualNavigation from "./nav/ContextualNavigation";
import ActivityNavigation from "./nav/ActivityNavigation";
import {TemplateContext} from "@common/context/TemplateContext";
import {BrandContext} from "@context/BrandContext";
import {UserContext} from "@context/UserContext";
import {ShortcutContext} from "@common/context/ShortcutContext";
import {ContentContext} from "@context/ContentContext";
import ErrorFallback from "@common/ErrorFallback";
import Portal from "@common/Portal";
import Header from "./nav/Header";
import NavigationRight from "./nav/NavigationRight";
import CircularLoader from "@common/CircularLoader";
import GenericDialog from "@common/GenericDialog";
import ContextualNavigationDevelopment from "./nav/ContextualNavigationDevelopment";
import HeaderDevelopment from "./nav/HeaderDevelopment";
import ActivityNavigationDevelopment from "./nav/ActivityNavigationDevelopment";
import FloatingHelp from "./nav/FloatingHelp";
import AccessibilitySkip from "./nav/AccessibilitySkip";
import FloatingScrollToTop from "./nav/FloatingScrollToTop";
import FloatingCookieManager from "./nav/FloatingCookieManager";
import {NavigationContext} from "@common/context/NavigationContext";
import {Helmet} from "react-helmet";
import {removeAntdSelectTitles} from "./utils/removeAntdSelectTitles";
import {setupAntdSelectOptionFocusHandler} from "./utils/setupAntdSelectOptionFocusHandler";
import {setupClearIconTabIndex} from "./utils/setupClearIconTabIndex";
import MediaPlayerDialog from "./media/MediaPlayerDialog";
import {useScreenMode} from "@common/context/ScreenModeContext";

export const SMALL_SCREEN_SIZE = 1024;

const App = props => {
    const mobileScreenSize = 768;
    const {singlePage = true, sessionExpired, bodyComponentName, name} = useContext(TemplateContext);
    const brandConfig = useContext(BrandContext);
    const {user, updateUser, updateRightMenuClosed, menuPosition} = useContext(UserContext);
    const {globalShortcuts, contextualShortcuts, activityShortcuts} = useContext(ShortcutContext);
    const {content, dialogMediaObject} = useContext(ContentContext);
    const {isNavVisible, setNavVisible, isDrawerVisible} = useContext(NavigationContext);
    const {smallScreenMode, mobileScreenMode} = useScreenMode();
    const [scrollingDown, setScrollingDown] = useState(false);
    const [lastScrollTop, setLastScrollTop] = useState(0);
    const contentRef = useRef(null);
    const [applyNavTransition, setApplyNavTransition] = useState(false);

    useEffect(() => {
        removeAntdSelectTitles();
        setupAntdSelectOptionFocusHandler();

        const observer = setupClearIconTabIndex();
        return () => {
            observer.disconnect()
        }
    }, []);

    const toggleRightNavCompressed = () => {
        updateRightMenuClosed(!menuPosition.rightMenuClosed);
    }

    const handleStorageEvent = (e) => {
        if(e.key == 'conceptId' && user != undefined && !user.publicSession && content !== undefined && content.currentConcept !== undefined && content.currentConcept.conceptId != e.newValue) {
            location.reload();
        }

        if(e.key == 'startTime' && user != undefined && user.student && !user.publicSession) {
            displayToastMessage('Multiple tabs are open', 'You have opened another tab. This can cause problems. Please only use a single tab.', 'error', undefined, 30000, false);
        }
    };


    const errorHandler = (error, info) => {
        if (typeof newrelic !== 'undefined') {
            newrelic.noticeError('React error' + error);
            newrelic.noticeError(error);
        }
        console.error(error);
    }

    const handleScroll = () => {
        const ref = contentRef.current;
        const currentScrollTop = ref.scrollTop;

        const content = document.getElementById("content");
        if (!content) return;

        const contentStyle = window.getComputedStyle(content);
        const paddingTop = parseInt(contentStyle.getPropertyValue("padding-top"), 10);

        // Check if the content cannot scroll
        if (content.offsetHeight >= ref.scrollHeight) {
            setScrollingDown(false);
            return;
        }

        // Always restore toolbar when at the very top
        if (currentScrollTop === 0) {
            setScrollingDown(false);
            setLastScrollTop(0);
            return;
        }

        // Prevent toggling near the top or bottom of the scrollable content
        if (currentScrollTop < 96 || currentScrollTop >= ref.scrollHeight - ref.clientHeight - paddingTop) {
            return;
        }

        if (currentScrollTop > lastScrollTop) {
            setScrollingDown(true);
        } else if (currentScrollTop < lastScrollTop) {
            setScrollingDown(false);
        }

        setLastScrollTop(currentScrollTop);
    };



    useEffect(() => {
        window.removeEventListener('storage', handleStorageEvent);
        if(content !== undefined && content.currentConcept !== undefined) {
            window.addEventListener('storage', handleStorageEvent);
        }

        if(content?.currentConcept !== undefined) {
            updateUser({
                conceptId: content.currentConcept.conceptId,
                topicId: content.currentConcept.topicId,
                syllabusId: content.currentConcept.syllabusId,
                syllabusName: content.currentConcept.syllabusName,
                syllabusGroupId: content.currentSyllabusGroup.syllabusGroupId,
                syllabusGroupName: content.currentSyllabusGroup.syllabusGroupId
            });
        }
    }, [content]);

    useEffect(() => {
        if (!brandConfig) return;

        const ref = contentRef.current;
        if (brandConfig.useDevelopmentNavigation && ref) {
            ref.addEventListener("scroll", handleScroll);
        }

        return () => {
            if (ref) {
                ref.removeEventListener("scroll", handleScroll);
            }
        };
    }, [brandConfig, handleScroll]);

    useEffect(() => {
        if(!smallScreenMode) setApplyNavTransition(false);
        const transitionTimer = setTimeout(() => {
            if(smallScreenMode) setApplyNavTransition(true);
        },500)

        return () => {
            clearTimeout(transitionTimer)
        }
    },[smallScreenMode])

    $('.js-contentLoader').hide();
    if(brandConfig === undefined || user === undefined || globalShortcuts === undefined || globalShortcuts.length == 0) {
        return (<CircularLoader/>);
    }
    $('.js-content').show();

    const generatePageTitle = () => {
        if(bodyComponentName === 'Lesson' && content.currentConcept?.conceptName){
            return(`${content.currentConcept.conceptName} | ${user.brandName}`)
        } else {
            return(`${name} | ${user.brandName}`)
        }
    }

    return (
        <>
            <Helmet>
                <title>{generatePageTitle()}</title>
            </Helmet>
            {brandConfig.useDevelopmentNavigation && !user.expired && !smallScreenMode &&
                <AccessibilitySkip contentRef={contentRef} />
            }
            {singlePage && (
                <>
                    {smallScreenMode && (
                        <header id={'header'} className={`ch-ribbon-header ${!isDrawerVisible && scrollingDown ? ' hidden' : ''}`} style={{backgroundImage: 'url(' + content.backgroundImage + ')', backgroundRepeat: 'no-repeat', backgroundSize: 'cover', backgroundPosition: 'center center'}}>
                            <ErrorBoundary
                                FallbackComponent={ErrorFallback}
                                onError={errorHandler}>
                                {brandConfig.useDevelopmentNavigation
                                    ? <HeaderDevelopment navRightCompressed={menuPosition.rightMenuClosed && (smallScreenMode || !user.guideMode)} setNavVisible={setNavVisible} toggleRightNavCompressed={toggleRightNavCompressed}/>
                                    : <Header navRightCompressed={menuPosition.rightMenuClosed && (smallScreenMode || !user.guideMode)} toggleNav={() => {setNavVisible(!isNavVisible)}} toggleRightNavCompressed={toggleRightNavCompressed}/>
                                }
                            </ErrorBoundary>
                        </header>
                    )}
                    <div className={'ch-base' + (menuPosition.leftMenuClosed && !smallScreenMode? ' compressed' : '') + (smallScreenMode && isNavVisible ? ' ch-no-overflow' : '')}>
                        <div id={'nav'} className={`${isNavVisible ? 'ch-nav-visible' : ''} ${applyNavTransition ? 'ch-nav-transition' : ''}`}>
                            <ErrorBoundary
                                FallbackComponent={ErrorFallback}
                                onError={errorHandler}>
                                {brandConfig.useDevelopmentNavigation ?
                                    <NavigationDevelopment
                                        visible={isNavVisible}
                                        updateVisible={(visible) => {setNavVisible(visible)}}
                                        contextualShortcuts={contextualShortcuts}
                                    >
                                        <ContextualNavigationDevelopment contextualShortcuts={contextualShortcuts} compressed={menuPosition.leftMenuClosed && !smallScreenMode} />
                                    </NavigationDevelopment> :
                                    <Navigation
                                        smallScreenMode={smallScreenMode}
                                        visible={isNavVisible}
                                        updateVisible={(visible) => {setNavVisible(visible)}}
                                        globalShortcuts={globalShortcuts}
                                    >
                                        <ContextualNavigation contextualShortcuts={contextualShortcuts} compressed={menuPosition.leftMenuClosed && !smallScreenMode} />
                                    </Navigation>}
                            </ErrorBoundary>
                        </div>
                        <div
                            id="content"
                            className={
                                `ch-content` +
                                (smallScreenMode ? ' ch-small-screen' : '') +
                                (!smallScreenMode && brandConfig.useDevelopmentNavigation
                                    ? ((scrollingDown && !mobileScreenMode) && !isDrawerVisible ? ' ch-title-hidden' : ' ch-title-visible')
                                    : '') +
                                ((smallScreenMode && brandConfig.useDevelopmentNavigation) && !scrollingDown
                                    ? ' ch-header-visible'
                                    : ' ch-header-hidden')
                            }
                        >
                            <ErrorBoundary
                                FallbackComponent={ErrorFallback}
                                onError={errorHandler}>
                                {brandConfig.useDevelopmentNavigation ?
                                    !smallScreenMode && <Title/>
                                    : <Title/>
                                }
                            </ErrorBoundary>
                            <div
                                className={'ch-content-body' + ((menuPosition.rightMenuClosed && (smallScreenMode || !user.guideMode) || (brandConfig.useDevelopmentNavigation)) ? ' ch-nav-r-closed' : ' ch-nav-r-open')}>
                                <ErrorBoundary
                                    FallbackComponent={ErrorFallback}
                                    resetKeys={[bodyComponentName]}
                                    onError={errorHandler}>
                                    <Body ref={contentRef}/>
                                </ErrorBoundary>
                                <div id={'nav-r'} className={'ch-nav-r'}>
                                    <ErrorBoundary
                                        FallbackComponent={ErrorFallback}
                                        onError={errorHandler}>
                                        {(!brandConfig.useDevelopmentNavigation || (brandConfig.useDevelopmentNavigation && smallScreenMode)) &&
                                            <NavigationRight smallScreenMode={smallScreenMode}
                                                             compressed={menuPosition.rightMenuClosed && (smallScreenMode || !user.guideMode)}
                                                             callback={toggleRightNavCompressed}>
                                                {brandConfig.useDevelopmentNavigation ?
                                                    <ActivityNavigationDevelopment
                                                        activityShortcuts={activityShortcuts}/> :
                                                    <ActivityNavigation activityShortcuts={activityShortcuts}/>
                                                }
                                            </NavigationRight>
                                        }
                                    </ErrorBoundary>
                                </div>
                                {brandConfig.useDevelopmentNavigation && !(mobileScreenMode && isNavVisible) && !isDrawerVisible &&
                                    globalShortcuts.some(shortcut => shortcut.name === 'Help' || shortcut.name === 'Help widget') &&
                                    <FloatingHelp globalShortcuts={globalShortcuts}/>
                                }
                                {brandConfig.useCookieManager && !(mobileScreenMode && isNavVisible) && !user.cmsMode &&
                                    <FloatingCookieManager/>
                                }
                                {!(mobileScreenMode && isNavVisible) &&
                                    <FloatingScrollToTop scrollRef={contentRef} bottomPosition={(brandConfig.useCookieManager  && !user.cmsMode) ? '140px' : '80px'}/>
                                }
                            </div>
                        </div>
                    </div>
                    {sessionExpired &&
                        <GenericDialog
                            open={true}
                            title="Expired Session "
                            onClose={() => {
                                window.location.href = '/';
                            }}
                            buttons={[{
                                name: 'Continue'
                            }]}
                        >
                            <div style={{display: 'flex', alignItems: 'center'}}>
                                <UpdateIcon fontSize={'large'} sx={{margin: 1}}/> Your session has expired. Please log in again to continue.
                            </div>
                        </GenericDialog>
                    }
                    {dialogMediaObject &&
                        <MediaPlayerDialog open={true} />
                    }
                </>
            )}
            {!singlePage && (
                <>
                    <Portal id={'header'}>
                        {brandConfig.useDevelopmentNavigation
                        ? <Header navRightCompressed={menuPosition.rightMenuClosed && (smallScreenMode || !user.guideMode)} toggleNav={() => {setNavVisible(!isNavVisible)}} toggleRightNavCompressed={toggleRightNavCompressed}/>
                        : <HeaderDevelopment navRightCompressed={menuPosition.rightMenuClosed && (smallScreenMode || !user.guideMode)} toggleNav={() => {setNavVisible(!isNavVisible)}} toggleRightNavCompressed={toggleRightNavCompressed}/>
                        }
                    </Portal>
                    <Portal id={'nav'} className={(isNavVisible ? 'ch-nav-visible' : '') + (menuPosition.leftMenuClosed && !smallScreenMode? ' compressed' : '')}>
                        <Navigation
                            smallScreenMode={smallScreenMode}
                            visible={isNavVisible}
                            updateVisible={(visible) => {setNavVisible(visible)}}
                            globalShortcuts={globalShortcuts}
                        >
                            <ContextualNavigation contextualShortcuts={contextualShortcuts} compressed={menuPosition.leftMenuClosed} />
                        </Navigation>
                    </Portal>
                    <Portal id={'nav-r'} className={'ch-nav-r'}>
                        {!user.expired &&
                            !brandConfig.useDevelopmentNavigation ?
                                <NavigationRight compressed={menuPosition.rightMenuClosed && (smallScreenMode || !user.guideMode)} callback={toggleRightNavCompressed}>
                                    <ActivityNavigation activityShortcuts={activityShortcuts}/>
                                </NavigationRight>
                                :
                                <NavigationRight compressed={menuPosition.rightMenuClosed && (smallScreenMode || !user.guideMode)} callback={toggleRightNavCompressed}>
                                    <ActivityNavigationDevelopment activityShortcuts={activityShortcuts}/>
                                </NavigationRight>
                        }
                    </Portal>
                </>
            )}
        </>
    )
}

export default App;